import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  FiberManualRecordRounded,
  WarningAmberRounded,
} from '@mui/icons-material';

import type {
  StacTeamReportingDay,
} from '../types/stac';


interface StacTeamTodayPanelProps {
  data: StacTeamReportingDay | null;
  isLoading: boolean;
  isError: boolean;
}


function formatDuration(
  seconds: number,
): string {
  const totalMinutes =
    Math.floor(
      seconds / 60
    );

  const hours =
    Math.floor(
      totalMinutes / 60
    );

  const minutes =
    totalMinutes % 60;

  if (!hours) {
    return `${minutes} min`;
  }

  if (!minutes) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
}


function formatTime(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      hour: '2-digit',
      minute: '2-digit',
    },
  ).format(
    new Date(value)
  );
}


function formatDay(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    },
  ).format(
    new Date(
      `${value}T12:00:00`
    )
  );
}


export default function StacTeamTodayPanel({
  data,
  isLoading,
  isError,
}: StacTeamTodayPanelProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        border:
          '1px solid rgba(148,163,184,0.18)',
        bgcolor:
          'rgba(15,23,42,0.66)',
        backdropFilter:
          'blur(10px)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom:
            '1px solid rgba(148,163,184,0.14)',
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            sm: 'center',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 800,
              }}
            >
              Actividad de hoy
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                mt: 0.5,
                textTransform:
                  'capitalize',
              }}
            >
              {
                data?.date
                  ? formatDay(
                      data.date
                    )
                  : 'Seguimiento diario del equipo'
              }
            </Typography>
          </Box>

          <Chip
            label="Actualización automática"
            size="small"
            sx={{
              color: '#bae6fd',
              bgcolor:
                'rgba(56,189,248,0.10)',
              border:
                '1px solid rgba(56,189,248,0.18)',
              fontWeight: 800,
            }}
          />
        </Stack>
      </Box>

      {isLoading ? (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{
            minHeight: 220,
            px: 3,
            py: 4,
          }}
        >
          <CircularProgress
            sx={{
              color: '#38bdf8',
            }}
          />

          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              fontWeight: 600,
            }}
          >
            Cargando actividad del equipo...
          </Typography>
        </Stack>
      ) : isError ? (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            variant="outlined"
          >
            No se pudo cargar la actividad
            de hoy del equipo.
          </Alert>
        </Box>
      ) : !data?.workers.length ? (
        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
            }}
          >
            No hay personas dentro del
            ámbito de supervisión actual.
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: {
                xs: 'none',
                md: 'grid',
              },
              gridTemplateColumns:
                'minmax(190px, 1.05fr) '
                + 'minmax(150px, 0.8fr) '
                + 'minmax(240px, 1.35fr) '
                + '130px '
                + 'minmax(200px, 1fr)',
              columnGap: 2,
              alignItems: 'center',
              px: 3,
              py: 1.15,
              bgcolor:
                'rgba(15,23,42,0.56)',
              borderBottom:
                '1px solid rgba(148,163,184,0.12)',
            }}
          >
            {[
              'Persona',
              'Unidad',
              'Actividad actual',
              'Hoy',
              'Última actividad',
            ].map(
              (label) => (
                <Typography
                  key={label}
                  sx={{
                    color: '#64748b',
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    textTransform:
                      'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {label}
                </Typography>
              ),
            )}
          </Box>

          {data.workers.map(
            (worker) => {
              const active =
                worker.active_entry;

              const last =
                worker.last_entry;

              return (
                <Box
                  key={
                    worker.worker_id
                  }
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md:
                        'minmax(190px, 1.05fr) '
                        + 'minmax(150px, 0.8fr) '
                        + 'minmax(240px, 1.35fr) '
                        + '130px '
                        + 'minmax(200px, 1fr)',
                    },
                    columnGap: 2,
                    rowGap: {
                      xs: 1.25,
                      md: 0,
                    },
                    alignItems: 'center',
                    px: 3,
                    py: 1.25,
                    borderBottom:
                      '1px solid rgba(148,163,184,0.07)',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      bgcolor:
                        'rgba(255,255,255,0.025)',
                    },
                  }}
                >
                  <Box>
                    <Stack
                      direction="row"
                      spacing={0.65}
                      alignItems="center"
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 800,
                          fontSize: '0.875rem',
                        }}
                      >
                        {
                          worker
                            .display_name
                        }
                      </Typography>

                      {worker.is_viewer ? (
                        <Chip
                          size="small"
                          label="Tú"
                          sx={{
                            height: 22,
                            bgcolor:
                              'rgba(148,163,184,0.12)',
                            color: '#cbd5e1',
                            fontWeight: 800,
                          }}
                        />
                      ) : null}

                      {worker.has_overlaps ? (
                        <Chip
                          size="small"
                          icon={
                            <WarningAmberRounded
                              sx={{
                                fontSize:
                                  '0.9rem !important',
                              }}
                            />
                          }
                          label="Revisar"
                          color="warning"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontWeight: 800,
                          }}
                        />
                      ) : null}
                    </Stack>

                    <Typography
                      sx={{
                        display: {
                          xs: 'block',
                          md: 'none',
                        },
                        mt: 0.3,
                        color: '#64748b',
                        fontSize: '0.78rem',
                      }}
                    >
                      {worker.unit_name}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      display: {
                        xs: 'none',
                        md: 'block',
                      },
                      color: '#94a3b8',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    {worker.unit_name}
                  </Typography>

                  <Box>
                    {active ? (
                      <>
                        <Stack
                          direction="row"
                          spacing={0.6}
                          alignItems="center"
                        >
                          <FiberManualRecordRounded
                            sx={{
                              color: '#22c55e',
                              fontSize: 11,
                            }}
                          />

                          <Typography
                            noWrap
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 800,
                              fontSize: '0.875rem',
                            }}
                          >
                            {
                              active
                                .task_title
                            }
                          </Typography>
                        </Stack>

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#64748b',
                            mt: 0.25,
                          }}
                        >
                          Desde las{' '}
                          {
                            formatTime(
                              active
                                .started_at
                            )
                          }
                        </Typography>
                      </>
                    ) : (
                      <Stack
                        direction="row"
                        spacing={0.6}
                        alignItems="center"
                      >
                        <FiberManualRecordRounded
                          sx={{
                            color: '#475569',
                            fontSize: 11,
                          }}
                        />

                        <Typography
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                          }}
                        >
                          Sin actividad
                        </Typography>
                      </Stack>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                      fontSize: '0.875rem',
                    }}
                  >
                    {
                      formatDuration(
                        worker
                          .total_seconds
                      )
                    }
                  </Typography>

                  <Box>
                    {last ? (
                      <>
                        <Typography
                          noWrap
                          sx={{
                            color: '#cbd5e1',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                          }}
                        >
                          {
                            last
                              .task_title
                          }
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#64748b',
                            mt: 0.25,
                          }}
                        >
                          {
                            formatTime(
                              last
                                .started_at
                            )
                          }
                          {' → '}
                          {
                            last.ended_at
                              ? formatTime(
                                  last
                                    .ended_at
                                )
                              : 'Ahora'
                          }
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: '0.875rem',
                        }}
                      >
                        Sin registros hoy
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            },
          )}
        </Box>
      )}
    </Paper>
  );
}
